import React, { useEffect, useRef, useState } from 'react';
import Parse from '../../parseConfig';
import { getListingEditorRole } from './userHasListingEditorRole';
import '../styles/StockListingAdminModal.css';
import '../styles/phcolours.css';
import TrashIcon from '../../images/trash.svg';

const HouseLand = Parse.Object.extend('HouseLand');
const MapLayer = Parse.Object.extend('MapLayer');
const PROGRESS_PRESETS = [
  'Coming Soon',
  'Design/Consenting',
  'Foundation Complete',
  'Under Construction',
  'Build Complete',
];

const UploadIcon = () => (
  <svg aria-hidden="true" viewBox="0 0 20 20" focusable="false">
    <path d="M10 3.5 5.8 7.7l1.1 1.1 2.3-2.3v7h1.6v-7l2.3 2.3 1.1-1.1L10 3.5Z" />
    <path d="M4.5 15h11v1.5h-11V15Z" />
  </svg>
);

const getParseFileName = (file) => {
  if (!file) return '';

  const fileName =
    typeof file.name === 'function'
      ? file.name()
      : file._name || file.name || '';

  const separatorIndex = fileName.indexOf('_');
  if (separatorIndex === -1) {
    return fileName;
  }

  return fileName.slice(separatorIndex + 1);
};

const formatCoordinates = (lat, lng) => {
  if (lat === null || lat === undefined || lng === null || lng === undefined || lat === '' || lng === '') {
    return '';
  }

  return `${lat},${lng}`;
};

const parseCoordinates = (value) => {
  const trimmedValue = String(value || '').trim();

  if (!trimmedValue) {
    return null;
  }

  const parts = trimmedValue.split(',');

  if (parts.length !== 2) {
    throw new Error('Coordinates must be in the format latitude,longitude.');
  }

  const latitude = Number(parts[0].trim());
  const longitude = Number(parts[1].trim());

  if (Number.isNaN(latitude) || Number.isNaN(longitude)) {
    throw new Error('Coordinates must be valid numbers in the format latitude,longitude.');
  }

  return { latitude, longitude };
};

const createEmptyDraft = () => ({
  id: null,
  displayTitle: '',
  locationString: '',
  price: '',
  availability: '',
  progress: '',
  buildSpec: '',
  franchise: '',
  trademeURL: '',
  bedrooms: '',
  bathrooms: '',
  floorArea: '',
  landArea: '',
  coordinates: '',
  thumbnailName: '',
  thumbnailUploadFile: null,
  removeThumbnail: false,
  brochureName: '',
  brochureUploadFile: null,
  removeBrochure: false,
  associatedMapLayerId: '',
  associatedMapLayerName: '',
  inactive: false,
  _raw: null,
});

const createDraftFromListing = (listing) => {
  const thumbFile = listing._raw?.get?.('thumb');
  const brochureFile = listing._raw?.get?.('brochure');

  return {
    id: listing.id,
    displayTitle: listing.displayTitle || '',
    locationString: listing.locationString || '',
    price: listing.price || '',
    availability: listing.availability || '',
    progress: listing.progress || '',
    buildSpec: listing.buildSpec || '',
    franchise: listing.franchise || '',
    trademeURL: listing.trademeURL || '',
    bedrooms: listing.bedrooms ?? '',
    bathrooms: listing.bathrooms ?? '',
    floorArea: listing.floorArea ?? '',
    landArea: listing.landArea ?? '',
    coordinates: formatCoordinates(listing.coordinates?.lat, listing.coordinates?.lng),
    thumbnailName: listing.thumbnailName || getParseFileName(thumbFile),
    thumbnailUploadFile: null,
    removeThumbnail: false,
    brochureName: listing.brochureName || getParseFileName(brochureFile),
    brochureUploadFile: null,
    removeBrochure: false,
    associatedMapLayerId: listing.associatedMapLayerId || '',
    associatedMapLayerName: listing.associatedMapLayerName || listing._raw?.get?.('associatedMapLayer')?.get?.('name') || '',
    inactive: listing.inactive === true,
    _raw: listing._raw || null,
  };
};

const getStatusLabel = (listing) => {
  if (listing.availability) return listing.availability;
  if (listing.progress) return listing.progress;
  return 'Draft';
};

export default function StockListingAdminModal({
  isOpen,
  listings = [],
  initialListing = null,
  hideListPanel = false,
  onClose = () => {},
  onListingsChanged = async () => {},
}) {
  const [activeDraft, setActiveDraft] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showInactive, setShowInactive] = useState(true);
  const [sortOrder, setSortOrder] = useState('newest');
  const [isProgressMenuOpen, setIsProgressMenuOpen] = useState(false);
  const [isMapLayerMenuOpen, setIsMapLayerMenuOpen] = useState(false);
  const [mapLayerOptions, setMapLayerOptions] = useState([]);
  const [isLoadingMapLayers, setIsLoadingMapLayers] = useState(false);
  const progressComboboxRef = useRef(null);
  const mapLayerComboboxRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      setActiveDraft(null);
      setIsSaving(false);
      setSaveError('');
      setSearchQuery('');
      setShowInactive(true);
      setSortOrder('newest');
      setIsProgressMenuOpen(false);
      setIsMapLayerMenuOpen(false);
      setMapLayerOptions([]);
      setIsLoadingMapLayers(false);
      return;
    }

    if (hideListPanel && initialListing) {
      setActiveDraft(createDraftFromListing(initialListing));
      setSaveError('');
    }
  }, [hideListPanel, initialListing, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    let isCancelled = false;

    const loadMapLayerOptions = async () => {
      setIsLoadingMapLayers(true);

      try {
        const query = new Parse.Query(MapLayer);
        query.equalTo('type', 'subdivision');
        query.equalTo('active', 1);
        query.ascending('name');
        query.select(['name']);

        const results = await query.find();

        if (!isCancelled) {
          setMapLayerOptions(
            results.map((layer) => ({
              id: layer.id,
              name: layer.get('name') || 'Untitled map layer',
            }))
          );
        }
      } catch (error) {
        console.error('Failed to load associated map layer options:', error);
        if (!isCancelled) {
          setMapLayerOptions([]);
        }
      } finally {
        if (!isCancelled) {
          setIsLoadingMapLayers(false);
        }
      }
    };

    loadMapLayerOptions();

    return () => {
      isCancelled = true;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (activeDraft && !hideListPanel) {
          setActiveDraft(null);
          setSaveError('');
          return;
        }
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeDraft, hideListPanel, isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      if (progressComboboxRef.current?.contains(event.target)) {
        return;
      }

      if (mapLayerComboboxRef.current?.contains(event.target)) {
        return;
      }

      setIsProgressMenuOpen(false);
      setIsMapLayerMenuOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const normalizedSearchQuery = searchQuery.trim().toLowerCase();
  const listingsVisibleByInactiveState = showInactive
    ? listings
    : listings.filter((listing) => listing.inactive !== true);

  const searchedListings = normalizedSearchQuery
    ? listingsVisibleByInactiveState.filter((listing) => {
        const title = String(listing.displayTitle || '').toLowerCase();
        const location = String(listing.locationString || '').toLowerCase();
        return title.includes(normalizedSearchQuery) || location.includes(normalizedSearchQuery);
      })
    : listingsVisibleByInactiveState;

  const filteredListings = [...searchedListings].sort((a, b) => {
    const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;

    if (sortOrder === 'oldest') {
      return aTime - bTime;
    }

    return bTime - aTime;
  });

  const openNewListing = () => {
    setActiveDraft(createEmptyDraft());
    setSaveError('');
  };

  const openExistingListing = (listing) => {
    setActiveDraft(createDraftFromListing(listing));
    setSaveError('');
  };

  const closeEditor = () => {
    if (isSaving) return;

    if (hideListPanel) {
      onClose();
      return;
    }

    setActiveDraft(null);
    setSaveError('');
  };

  const updateDraftField = (field, value) => {
    setActiveDraft((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const updateThumbnailFile = (file) => {
    if (!file) return;

    setActiveDraft((current) => ({
      ...current,
      thumbnailName: file.name,
      thumbnailUploadFile: file,
      removeThumbnail: false,
    }));
  };

  const removeThumbnailFile = () => {
    setActiveDraft((current) => ({
      ...current,
      thumbnailName: '',
      thumbnailUploadFile: null,
      removeThumbnail: true,
    }));
  };

  const updateBrochureFile = (file) => {
    if (!file) return;

    setActiveDraft((current) => ({
      ...current,
      brochureName: file.name,
      brochureUploadFile: file,
      removeBrochure: false,
    }));
  };

  const removeBrochureFile = () => {
    setActiveDraft((current) => ({
      ...current,
      brochureName: '',
      brochureUploadFile: null,
      removeBrochure: true,
    }));
  };

  const applyProgressPreset = (value) => {
    updateDraftField('progress', value);
    setIsProgressMenuOpen(false);
  };

  const selectedMapLayerName =
    mapLayerOptions.find((layer) => layer.id === activeDraft?.associatedMapLayerId)?.name ||
    activeDraft?.associatedMapLayerName ||
    '';

  const applyAssociatedMapLayer = (layerId) => {
    const selectedLayerName = mapLayerOptions.find((layer) => layer.id === layerId)?.name || '';
    setActiveDraft((current) => ({
      ...current,
      associatedMapLayerId: layerId,
      associatedMapLayerName: selectedLayerName,
    }));
    setIsMapLayerMenuOpen(false);
  };

  const setOptionalString = (object, field, value) => {
    const trimmed = typeof value === 'string' ? value.trim() : value;
    if (!trimmed) {
      object.unset(field);
      return;
    }
    object.set(field, trimmed);
  };

  const setOptionalNumber = (object, field, value) => {
    if (value === '' || value === null || value === undefined) {
      object.unset(field);
      return;
    }

    const numberValue = Number(value);
    if (Number.isNaN(numberValue)) {
      throw new Error(`Invalid value for ${field}`);
    }

    object.set(field, numberValue);
  };

  const getListingObjectForSave = async () => {
    if (!activeDraft?.id) {
      const listingObject = new HouseLand();
      const listingEditorRole = await getListingEditorRole();

      if (!listingEditorRole) {
        throw new Error('Listing editor role not found.');
      }

      const acl = new Parse.ACL();
      acl.setPublicReadAccess(true);
      acl.setRoleReadAccess(listingEditorRole, true);
      acl.setRoleWriteAccess(listingEditorRole, true);
      listingObject.setACL(acl);

      return listingObject;
    }

    const query = new Parse.Query(HouseLand);
    query.equalTo('objectId', activeDraft.id);

    const listingObject = await query.first();

    if (!listingObject) {
      throw new Error('Object not found.');
    }

    return listingObject;
  };

  const handleSave = async () => {
    if (!activeDraft) return;

    const trimmedTitle = activeDraft.displayTitle.trim();
    const trimmedLocation = activeDraft.locationString.trim();

    if (!trimmedTitle || !trimmedLocation) {
      setSaveError('Title and location are required.');
      return;
    }

    setIsSaving(true);
    setSaveError('');

    try {
      const listingObject = await getListingObjectForSave();

      listingObject.set('displayTitle', trimmedTitle);
      listingObject.set('locationString', trimmedLocation);
      setOptionalString(listingObject, 'price', activeDraft.price);
      setOptionalString(listingObject, 'availability', activeDraft.availability);
      setOptionalString(listingObject, 'progress', activeDraft.progress);
      setOptionalString(listingObject, 'buildSpec', activeDraft.buildSpec);
      setOptionalString(listingObject, 'franchise', activeDraft.franchise);
      setOptionalString(listingObject, 'trademeURL', activeDraft.trademeURL);

      setOptionalNumber(listingObject, 'bedrooms', activeDraft.bedrooms);
      setOptionalNumber(listingObject, 'bathrooms', activeDraft.bathrooms);
      setOptionalNumber(listingObject, 'floorArea', activeDraft.floorArea);
      setOptionalNumber(listingObject, 'landArea', activeDraft.landArea);

      const parsedCoordinates = parseCoordinates(activeDraft.coordinates);

      if (!parsedCoordinates) {
        listingObject.unset('coordinates');
      } else {
        listingObject.set('coordinates', new Parse.GeoPoint(parsedCoordinates));
      }

      if (activeDraft.thumbnailUploadFile) {
        listingObject.set(
          'thumb',
          new Parse.File(activeDraft.thumbnailUploadFile.name, activeDraft.thumbnailUploadFile)
        );
      } else if (activeDraft.removeThumbnail) {
        listingObject.unset('thumb');
      }

      if (activeDraft.brochureUploadFile) {
        listingObject.set(
          'brochure',
          new Parse.File(activeDraft.brochureUploadFile.name, activeDraft.brochureUploadFile)
        );
      } else if (activeDraft.removeBrochure) {
        listingObject.unset('brochure');
      }

      if (activeDraft.associatedMapLayerId) {
        const associatedMapLayer = MapLayer.createWithoutData(activeDraft.associatedMapLayerId);
        listingObject.set('associatedMapLayer', associatedMapLayer);
      } else {
        listingObject.unset('associatedMapLayer');
      }

      if (activeDraft.inactive) {
        listingObject.set('inactive', 1);
      } else {
        listingObject.unset('inactive');
      }

      await listingObject.save();

      try {
        await onListingsChanged();
      } catch (refreshError) {
        console.error('Listing saved but the stock list failed to refresh:', refreshError);
      }

      if (hideListPanel) {
        onClose();
      } else {
        setActiveDraft(null);
      }
    } catch (error) {
      console.error('Failed to save stock listing:', error);
      if (error?.message === 'Object not found.') {
        setSaveError('This listing could not be found when saving. Please close the modal, refresh the list, and try again.');
      } else {
        setSaveError(error?.message || 'Failed to save listing.');
      }
    } finally {
      setIsSaving(false);
    }
  };

      return (
    <div className="stock-admin-overlay" onClick={onClose}>
      {!hideListPanel && (
        <div className="stock-admin-panel" onClick={(event) => event.stopPropagation()}>
          <div className="stock-admin-header">
            <div className="stock-admin-header-top-row">
              <div className="stock-admin-title">Listing Admin</div>
              <div className="stock-admin-header-actions">
                <button
                  type="button"
                  className="stock-admin-secondary-button stock-admin-new-button"
                  onClick={openNewListing}
                >
                  New Listing
                </button>
                <button type="button" className="stock-admin-close-button" onClick={onClose} aria-label="Close listing admin">
                  ×
                </button>
              </div>
            </div>

            <div className="stock-admin-header-main">
              <input
                type="text"
                className="stock-admin-search"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search listings"
              />
              <div className="stock-admin-controls-row">
                <label className="stock-admin-sort-wrap">
                  <select
                    className="stock-admin-sort-select"
                    value={sortOrder}
                    onChange={(event) => setSortOrder(event.target.value)}
                  >
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                  </select>
                </label>

                <label className="stock-admin-filter-toggle">
                  <input
                    type="checkbox"
                    checked={showInactive}
                    onChange={(event) => setShowInactive(event.target.checked)}
                  />
                  <span>Show Inactive</span>
                </label>

                <div className="stock-admin-list-count">
                  {filteredListings.length} {filteredListings.length === 1 ? 'listing' : 'listings'}
                </div>
              </div>
            </div>
          </div>

          <div className="stock-admin-list">
            {filteredListings.map((listing) => (
              <button
                type="button"
                key={listing.id}
                className="stock-admin-row"
                onClick={() => openExistingListing(listing)}
              >
                <div className="stock-admin-row-copy">
                  <div className="stock-admin-row-title">{listing.displayTitle || 'Untitled listing'}</div>
                  <div className="stock-admin-row-location">{listing.locationString || 'No location set'}</div>
                </div>
                <div className="stock-admin-row-meta">
                  {listing.inactive && (
                    <span className="stock-admin-status-pill stock-admin-status-pill-inactive">Inactive</span>
                  )}
                  <span className="stock-admin-status-pill">{getStatusLabel(listing)}</span>
                  <span className="stock-admin-chevron">›</span>
                </div>
              </button>
            ))}

            {listings.length === 0 && (
              <div className="stock-admin-empty">No listings found for this stock page.</div>
            )}

            {listings.length > 0 && filteredListings.length === 0 && (
              <div className="stock-admin-empty">No listings match that search.</div>
            )}
          </div>
        </div>
      )}

      {activeDraft && (
        <div className="stock-admin-editor-panel" onClick={(event) => event.stopPropagation()}>
          <div className="stock-admin-header stock-admin-editor-header">
            <div>
              <div className="stock-admin-title">
                {activeDraft.id ? 'Edit Listing' : 'New Listing'}
              </div>
            </div>
          </div>

          <div className="stock-admin-form">
            <label className="stock-admin-field stock-admin-field-full stock-admin-field-title">
              <span>Title</span>
              <input
                type="text"
                value={activeDraft.displayTitle}
                onChange={(event) => updateDraftField('displayTitle', event.target.value)}
              />
            </label>

            <label className="stock-admin-field stock-admin-field-full">
              <span>Location</span>
              <input
                type="text"
                value={activeDraft.locationString}
                onChange={(event) => updateDraftField('locationString', event.target.value)}
              />
            </label>

            <label className="stock-admin-field">
              <span>Price</span>
              <input
                type="text"
                value={activeDraft.price}
                onChange={(event) => updateDraftField('price', event.target.value)}
              />
            </label>

            <label className="stock-admin-field">
              <span>TradeMe URL</span>
              <input
                type="url"
                value={activeDraft.trademeURL}
                onChange={(event) => updateDraftField('trademeURL', event.target.value)}
              />
            </label>

            <label className="stock-admin-field">
              <span>Availability</span>
              <input
                type="text"
                value={activeDraft.availability}
                onChange={(event) => updateDraftField('availability', event.target.value)}
              />
            </label>

            <label className="stock-admin-field">
              <div className="stock-admin-field-label">
                <span>Progress</span>
                <span className="stock-admin-info-wrap">
                  <button
                    type="button"
                    className="stock-admin-info-button"
                    aria-label='Entering "Coming Soon" here puts the listing in a preview state in the sidebar and hides some info.'
                  >
                    i
                  </button>
                  <span className="stock-admin-info-tooltip">
                    Entering "Coming Soon" here puts the listing in a preview state in the sidebar and hides some info.
                  </span>
                </span>
              </div>
              <div className="stock-admin-combobox" ref={progressComboboxRef}>
                <input
                  type="text"
                  className="stock-admin-combobox-input"
                  value={activeDraft.progress}
                  onChange={(event) => updateDraftField('progress', event.target.value)}
                />
                <button
                  type="button"
                  className="stock-admin-combobox-toggle"
                  aria-label="Show progress presets"
                  aria-expanded={isProgressMenuOpen}
                  onClick={() => setIsProgressMenuOpen((current) => !current)}
                >
                  <span>▾</span>
                </button>
                {isProgressMenuOpen && (
                  <div className="stock-admin-combobox-menu stock-admin-progress-menu">
                    {PROGRESS_PRESETS.map((preset) => (
                      <button
                        key={preset}
                        type="button"
                        className="stock-admin-combobox-option"
                        onClick={() => applyProgressPreset(preset)}
                      >
                        {preset}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </label>

            <label className="stock-admin-field">
              <span>Build Spec</span>
              <input
                type="text"
                value={activeDraft.buildSpec}
                onChange={(event) => updateDraftField('buildSpec', event.target.value)}
              />
            </label>

            <label className="stock-admin-field">
              <span>Franchise</span>
              <input
                type="text"
                value={activeDraft.franchise}
                onChange={(event) => updateDraftField('franchise', event.target.value)}
              />
            </label>

            <div className="stock-admin-field-pair">
              <label className="stock-admin-field stock-admin-field-compact">
                <span>Bedrooms</span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={activeDraft.bedrooms}
                  onChange={(event) => updateDraftField('bedrooms', event.target.value)}
                />
              </label>

              <label className="stock-admin-field stock-admin-field-compact">
                <span>Bathrooms</span>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={activeDraft.bathrooms}
                  onChange={(event) => updateDraftField('bathrooms', event.target.value)}
                />
              </label>
            </div>

            <div className="stock-admin-field-pair">
              <label className="stock-admin-field stock-admin-field-compact">
                <span>Floor Area (m²)</span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={activeDraft.floorArea}
                  onChange={(event) => updateDraftField('floorArea', event.target.value)}
                />
              </label>

              <label className="stock-admin-field stock-admin-field-compact">
                <span>Land Area (m²)</span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={activeDraft.landArea}
                  onChange={(event) => updateDraftField('landArea', event.target.value)}
                />
              </label>
            </div>

            <div className="stock-admin-field">
              <span>Thumbnail</span>
              {activeDraft.thumbnailName ? (
                <div className="stock-admin-file-control">
                  <span className="stock-admin-file-name" title={activeDraft.thumbnailName}>
                    {activeDraft.thumbnailName}
                  </span>

                  <div className="stock-admin-file-actions">
                    <button
                      type="button"
                      className="stock-admin-icon-button stock-admin-trash-button"
                      onClick={removeThumbnailFile}
                      aria-label="Remove thumbnail"
                      title="Remove thumbnail"
                    >
                      <img src={TrashIcon} alt="" className="stock-admin-trash-icon" />
                    </button>
                  </div>
                </div>
              ) : (
                <label className="stock-admin-file-control stock-admin-file-control-empty">
                  <span className="stock-admin-file-upload-label">Upload</span>

                  <div className="stock-admin-file-actions">
                    <span className="stock-admin-icon-button" aria-label="Upload thumbnail" title="Upload thumbnail">
                    <input
                      type="file"
                      accept="image/*"
                      className="stock-admin-file-input"
                      onChange={(event) => {
                        updateThumbnailFile(event.target.files?.[0]);
                        event.target.value = '';
                      }}
                    />
                    <UploadIcon />
                    </span>
                  </div>
                </label>
              )}
            </div>

            <div className="stock-admin-field">
              <span>Brochure</span>
              {activeDraft.brochureName ? (
                <div className="stock-admin-file-control">
                  <span className="stock-admin-file-name" title={activeDraft.brochureName}>
                    {activeDraft.brochureName}
                  </span>

                  <div className="stock-admin-file-actions">
                    <button
                      type="button"
                      className="stock-admin-icon-button stock-admin-trash-button"
                      onClick={removeBrochureFile}
                      aria-label="Remove brochure"
                      title="Remove brochure"
                    >
                      <img src={TrashIcon} alt="" className="stock-admin-trash-icon" />
                    </button>
                  </div>
                </div>
              ) : (
                <label className="stock-admin-file-control stock-admin-file-control-empty">
                  <span className="stock-admin-file-upload-label">Upload</span>

                  <div className="stock-admin-file-actions">
                    <span className="stock-admin-icon-button" aria-label="Upload brochure" title="Upload brochure">
                    <input
                      type="file"
                      className="stock-admin-file-input"
                      onChange={(event) => {
                        updateBrochureFile(event.target.files?.[0]);
                        event.target.value = '';
                      }}
                    />
                    <UploadIcon />
                    </span>
                  </div>
                </label>
              )}
            </div>

            <div className="stock-admin-field">
              <span>Associated Map Layer</span>
              <div className="stock-admin-combobox" ref={mapLayerComboboxRef}>
                <button
                  type="button"
                  className={`stock-admin-combobox-input stock-admin-select-button${activeDraft.associatedMapLayerId ? '' : ' stock-admin-select-placeholder'}`}
                  onClick={() => setIsMapLayerMenuOpen((current) => !current)}
                  disabled={isLoadingMapLayers}
                  aria-expanded={isMapLayerMenuOpen}
                >
                  {isLoadingMapLayers
                    ? 'Loading map layers...'
                    : selectedMapLayerName || 'Select map layer'}
                </button>
                <button
                  type="button"
                  className="stock-admin-combobox-toggle"
                  aria-label="Show map layer options"
                  aria-expanded={isMapLayerMenuOpen}
                  onClick={() => setIsMapLayerMenuOpen((current) => !current)}
                  disabled={isLoadingMapLayers}
                >
                  <span>▾</span>
                </button>
                {isMapLayerMenuOpen && (
                  <div className="stock-admin-combobox-menu">
                    <button
                      type="button"
                      className="stock-admin-combobox-option"
                      onClick={() => applyAssociatedMapLayer('')}
                    >
                      None
                    </button>
                    {mapLayerOptions.map((layer) => (
                      <button
                        key={layer.id}
                        type="button"
                        className="stock-admin-combobox-option"
                        onClick={() => applyAssociatedMapLayer(layer.id)}
                      >
                        {layer.name}
                      </button>
                    ))}
                    {!isLoadingMapLayers && mapLayerOptions.length === 0 && (
                      <button type="button" className="stock-admin-combobox-option" disabled>
                        No subdivision layers found
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            <label className="stock-admin-field">
              <span>Coordinates</span>
              <input
                type="text"
                value={activeDraft.coordinates}
                onChange={(event) => updateDraftField('coordinates', event.target.value)}
                placeholder="-43.5321,172.6362"
              />
            </label>

            <label className="stock-admin-toggle">
              <input
                type="checkbox"
                checked={activeDraft.inactive}
                onChange={(event) => updateDraftField('inactive', event.target.checked)}
              />
              <span>Archive this listing</span>
            </label>

          </div>

          <div className="stock-admin-footer">
            <div className="stock-admin-footer-status">
              {saveError && <div className="stock-admin-error">{saveError}</div>}
            </div>
            <div className="stock-admin-footer-actions">
              <button type="button" className="stock-admin-secondary-button" onClick={closeEditor} disabled={isSaving}>
                Cancel
              </button>
              <button type="button" className="stock-admin-primary-button" onClick={handleSave} disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save Listing'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
